import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest, jsonBody } from '../../lib/api-client';
import type {
  AgendaValidation,
  Transaction,
  TransactionOverride,
  TransactionWrite,
} from '../../lib/api-types';

export const transactionKeys = {
  agenda: ['finance', 'transactions', 'agenda'] as const,
  all: ['finance', 'transactions'] as const,
  byCategory: (categoryId: string) => ['finance', 'transactions', 'category', categoryId] as const,
};

export async function fetchTransactions(categoryId?: string): Promise<Transaction[]> {
  const path = categoryId ? `/api/transactions/by-category/${categoryId}` : '/api/transactions';
  return (await apiRequest<{ transactions: Transaction[] }>(path)).transactions;
}

export async function fetchAgenda(): Promise<AgendaValidation[]> {
  return (await apiRequest<{ validations: AgendaValidation[] }>('/api/transactions/actuals'))
    .validations;
}

export async function createTransaction(input: TransactionWrite): Promise<Transaction> {
  return (
    await apiRequest<{ transaction: Transaction }>('/api/transactions', {
      body: jsonBody(input),
      method: 'POST',
    })
  ).transaction;
}

export async function updateTransaction(id: string, input: TransactionWrite): Promise<Transaction> {
  return (
    await apiRequest<{ transaction: Transaction }>(`/api/transactions/${id}`, {
      body: jsonBody(input),
      method: 'PUT',
    })
  ).transaction;
}

export function deleteTransaction(id: string): Promise<void> {
  return apiRequest(`/api/transactions/${id}`, { method: 'DELETE' });
}

export async function bulkUpdateTransactions(
  ids: string[],
  patch: { active?: boolean; categoryId?: string; walletId?: string },
): Promise<number> {
  return (
    await apiRequest<{ modifiedCount: number }>('/api/transactions/bulk-update', {
      body: jsonBody({ ids, patch }),
      method: 'POST',
    })
  ).modifiedCount;
}

export async function upsertOverride(
  id: string,
  date: string,
  input: Omit<TransactionOverride, 'date'>,
): Promise<Transaction> {
  return (
    await apiRequest<{ transaction: Transaction }>(`/api/transactions/${id}/overrides/${date}`, {
      body: jsonBody(input),
      method: 'PUT',
    })
  ).transaction;
}

export async function deleteOverride(id: string, date: string): Promise<Transaction> {
  return (
    await apiRequest<{ transaction: Transaction }>(`/api/transactions/${id}/overrides/${date}`, {
      method: 'DELETE',
    })
  ).transaction;
}

export async function setAgenda(
  id: string,
  occurrenceDate: string,
  status: AgendaValidation['status'],
): Promise<AgendaValidation> {
  return (
    await apiRequest<{ validation: AgendaValidation }>(`/api/transactions/${id}/agenda`, {
      body: jsonBody({ occurrenceDate, status }),
      method: 'PUT',
    })
  ).validation;
}

export function clearAgenda(id: string, occurrenceDate: string): Promise<void> {
  return apiRequest(`/api/transactions/${id}/agenda/${occurrenceDate}`, { method: 'DELETE' });
}

export function useTransactions(categoryId?: string) {
  return useQuery({
    queryFn: () => fetchTransactions(categoryId),
    queryKey: categoryId ? transactionKeys.byCategory(categoryId) : transactionKeys.all,
  });
}

export function useAgenda() {
  return useQuery({ queryFn: fetchAgenda, queryKey: transactionKeys.agenda });
}

function useTransactionInvalidation() {
  const client = useQueryClient();
  return async () => client.invalidateQueries({ queryKey: transactionKeys.all });
}

export function useCreateTransaction() {
  const invalidate = useTransactionInvalidation();
  return useMutation({ mutationFn: createTransaction, onSuccess: invalidate });
}

export function useUpdateTransaction() {
  const invalidate = useTransactionInvalidation();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TransactionWrite }) =>
      updateTransaction(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteTransaction() {
  const invalidate = useTransactionInvalidation();
  return useMutation({ mutationFn: deleteTransaction, onSuccess: invalidate });
}

export function useBulkUpdateTransactions() {
  const invalidate = useTransactionInvalidation();
  return useMutation({
    mutationFn: ({
      ids,
      patch,
    }: {
      ids: string[];
      patch: { active?: boolean; categoryId?: string; walletId?: string };
    }) => bulkUpdateTransactions(ids, patch),
    onSuccess: invalidate,
  });
}

export function useUpsertOverride() {
  const invalidate = useTransactionInvalidation();
  return useMutation({
    mutationFn: ({
      date,
      id,
      input,
    }: {
      date: string;
      id: string;
      input: Omit<TransactionOverride, 'date'>;
    }) => upsertOverride(id, date, input),
    onSuccess: invalidate,
  });
}

export function useDeleteOverride() {
  const invalidate = useTransactionInvalidation();
  return useMutation({
    mutationFn: ({ date, id }: { date: string; id: string }) => deleteOverride(id, date),
    onSuccess: invalidate,
  });
}

export function useSetAgenda() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      occurrenceDate,
      status,
    }: {
      id: string;
      occurrenceDate: string;
      status: AgendaValidation['status'];
    }) => setAgenda(id, occurrenceDate, status),
    onSuccess: async () => client.invalidateQueries({ queryKey: transactionKeys.agenda }),
  });
}

export function useClearAgenda() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, occurrenceDate }: { id: string; occurrenceDate: string }) =>
      clearAgenda(id, occurrenceDate),
    onSuccess: async () => client.invalidateQueries({ queryKey: transactionKeys.agenda }),
  });
}
